import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { APP_NAME } from '../../constants';
import Button from '../../components/ui/Button';
import HeroSection from './components/HeroSection';
import ServiceCategories from './components/ServiceCategories';
import HowItWorks from './components/HowItWorks';
import TestimonialsSection from './components/TestimonialsSection';
import TrustBadges from './components/TrustBadges';
import ServiceAreaMap from './components/ServiceAreaMap';
import PerformanceMetrics from './components/PerformanceMetrics';
import FaqSection from './components/FaqSection';
import { useAuth } from '../../contexts/AuthContext';

const LandingPage = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const handleBookService = () => {
    navigate('/service-booking-wizard');
  };

  const login = async () => {
    await signOut();
    navigate('/login-authentication');
  };

  const handleEmergencyCall = () => {
    window.location.href = 'tel:+33123456789';
  };

  // Navigation handlers
  const handleAccueil = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMobileMenuOpen(false);
  };
 

  const handleMonCompte = () => {
    navigate('/login-authentication');
    setMobileMenuOpen(false);
  };

  const handleReserver = () => {
    navigate('/service-booking-wizard');
    setMobileMenuOpen(false);
  };

  const handleContacter = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    setMobileMenuOpen(false);   
  };

  const handleFaq = () => {
    document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Services de Plomberie Professionnels 24/7 | Intervention Rapide</title>
        <meta 
          name="description" 
          content={`${APP_NAME} - Votre expert en plomberie disponible 24/7. Réparations d'urgence, installations, maintenance. Devis transparent, techniciens certifiés. Réservez en ligne maintenant.`} 
        />
        <meta name="keywords" content="plomberie, plombier, urgence plomberie, réparation fuite, installation sanitaire, maintenance plomberie" />
      </Helmet>

      <div className="marketing-shell">
        {/* Header - devient transparent au scroll (effet inverse) */}
        <header className={`marketing-header fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          !scrolled 
            ? ''
            : 'shadow-lg'
        }`}>
          <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
            {/* Logo et APP_NAME à gauche */}
            <div className="flex items-center gap-3">
              <div className="brand-mark w-10 h-10 transition-all duration-300">
                <Icon name="Droplet" size={22} color="#FFFFFF" />
              </div>
              <span className={`text-xl font-semibold transition-all duration-300 ${
                !scrolled ? 'text-white' : 'text-white'
              }`}>
                {APP_NAME}
              </span>
            </div>
            
            {/* Desktop Navigation - Au centre avec flex-1 et justify-center */}
            <div className="hidden md:flex flex-1 items-center justify-center gap-4">
              <button
                onClick={handleAccueil}
                className="transition-all duration-200 px-3 py-2 rounded-xl text-sm font-medium text-cyan-50/80 hover:text-cyan-100 hover:bg-cyan-300/10"
              >
                Accueil
              </button>
              <button
                onClick={handleMonCompte}
                className="transition-all duration-200 px-3 py-2 rounded-xl text-sm font-medium text-cyan-50/80 hover:text-cyan-100 hover:bg-cyan-300/10"
              >
                Mon compte
              </button>
              <button
                onClick={handleReserver}
                className="transition-all duration-200 px-3 py-2 rounded-xl text-sm font-medium text-cyan-50/80 hover:text-cyan-100 hover:bg-cyan-300/10"
              >
                Réserver
              </button>
              <button
                onClick={handleFaq}
                className="transition-all duration-200 px-3 py-2 rounded-xl text-sm font-medium text-cyan-50/80 hover:text-cyan-100 hover:bg-cyan-300/10"
              >
                FAQ
              </button>
              <button
                onClick={handleContacter}
                className="transition-all duration-200 px-3 py-2 rounded-xl text-sm font-medium text-cyan-50/80 hover:text-cyan-100 hover:bg-cyan-300/10"
              >
                Contacter
              </button>
            </div>
            
            {/* Bouton Connexion à droite */}
            <div className="hidden md:block">
              <Button
                variant="default"
                size="sm"
                iconName="User"
                onClick={login}
                className="transition-all duration-300"
              >
                Se connecter
              </Button>
            </div>

            {/* Mobile Menu Button (Hamburger) */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex flex-col gap-1.5 p-2 rounded-lg hover:bg-white/10 transition-all duration-300"
              aria-label="Menu"
            >
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          <div className={`md:hidden transition-all duration-300 overflow-hidden ${
            mobileMenuOpen ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="px-4 py-4 space-y-2 bg-[rgba(6,24,33,0.96)] backdrop-blur-xl border-t border-cyan-300/10">
              <button
                onClick={handleAccueil}
                className="w-full text-left px-4 py-3 rounded-lg text-white hover:bg-white/10 transition-all duration-300"
              >
                Accueil
              </button>
              <button
                onClick={handleMonCompte}
                className="w-full text-left px-4 py-3 rounded-lg text-white hover:bg-white/10 transition-all duration-300"
              >
                Mon compte
              </button>
              <button
                onClick={handleReserver}
                className="w-full text-left px-4 py-3 rounded-lg text-white hover:bg-white/10 transition-all duration-300"
              >
                Réserver
              </button>
              <button
                onClick={handleFaq}
                className="w-full text-left px-4 py-3 rounded-lg text-white hover:bg-white/10 transition-all duration-300"
              >
                FAQ
              </button>
              <button
                onClick={handleContacter}
                className="w-full text-left px-4 py-3 rounded-lg text-white hover:bg-white/10 transition-all duration-300"
              >
                Contacter
              </button>
              <div className="pt-2">
                <Button
                  variant="default"
                  size="sm"
                  iconName="User"
                  onClick={login}
                  className="w-full transition-all duration-300"
                >
                  Se connecter
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-16">
          <HeroSection onBookService={handleBookService} />
          <HowItWorks />
          <ServiceCategories onBookService={handleBookService} />
          <PerformanceMetrics />
          <TestimonialsSection />
          <TrustBadges />
          <ServiceAreaMap />
          <FaqSection />
          
          {/* CTA Section style portfolio */}
          <section className="marketing-section">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <div className="glass-panel rounded-3xl px-6 py-12 sm:px-12">
              <h2 className="marketing-title text-3xl lg:text-5xl font-heading font-bold mb-4">
                Prêt à résoudre votre problème de plomberie ?
              </h2>
              <p className="marketing-muted text-lg mb-8">
                Réservez votre intervention en quelques clics. Devis transparent, techniciens certifiés.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="xl"
                  iconName="Calendar"
                  onClick={handleBookService}
                >
                  Réserver maintenant
                </Button>
              </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer style portfolio */}
        <footer className="marketing-footer py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="brand-mark w-8 h-8">
                    <Icon name="Droplet" size={18} color="#FFFFFF" />
                  </div>
                  <span className="font-semibold text-white">{APP_NAME}</span>
                </div>
                <p className="text-sm text-gray-400">
                  Votre expert en plomberie disponible 24/7 pour tous vos besoins.
                </p>
                <br></br>
                <div className="flex flex-col gap-2">
                  <a href="/faq" className="marketing-link">
                    FAQ
                  </a>
                  <a href="/cgv" className="marketing-link">
                    CGV
                  </a>
                  <a href="/privacy-policy" className="marketing-link">
                    Politique de Confidentialité
                  </a>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-white mb-3">Services</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li>Réparations d'urgence</li>
                  <li>Installations</li>
                  <li>Maintenance préventive</li>
                  <li>Débouchage</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-white mb-3">Contact</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-center gap-2">
                    <Icon name="Mail" size={14} />
                    <span>contact@plumberpro.fr</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="MapPin" size={14} />
                    <span>Paris et Île-de-France</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-white mb-3">Horaires</h3>
                <p className="text-sm text-gray-400 mb-2">
                  Service disponible 24h/24, 7j/7
                </p>
                <p className="marketing-accent text-sm font-medium">
                  Interventions d'urgence garanties
                </p>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm text-gray-500">
              <p>© 2026 {APP_NAME}. Tous droits réservés. | Conformité RGPD | Assurance professionnelle</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;